import { useEffect } from "react";

const STORAGE_KEY = "saased.languagePref.v1";
export const LOAD_TRANSLATE_EVENT = "saased:load-translate";

/**
 * Loads the Google Translate element — but only for visitors who actually use
 * translation. The widget is ~100 KiB of JavaScript and costs a long main-thread
 * task, so English readers (the overwhelming majority) never download it.
 *
 * It loads when either:
 *  - a previous visit already switched language (googtrans cookie or the stored
 *    "switched" preference), so the translated page must be restored, or
 *  - the language dialog fires LOAD_TRANSLATE_EVENT because the visitor just
 *    chose another language.
 */
export function GoogleTranslateInit() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const ensureContainer = () => {
      if (!document.getElementById("google_translate_element")) {
        const div = document.createElement("div");
        div.id = "google_translate_element";
        div.style.display = "none";
        document.body.appendChild(div);
      }
    };

    let loaded = false;
    const loadTranslate = () => {
      if (loaded) return;
      loaded = true;
      if (document.getElementById("google_translate_script")) return;

      ensureContainer();

      const w = window as unknown as Record<string, unknown>;
      w.googleTranslateElementInit = () => {
        const google = (w as Record<string, unknown>).google as Record<string, unknown>;
        const translate = google.translate as Record<string, unknown>;
        const TranslateElement = translate.TranslateElement as new (
          opts: Record<string, unknown>,
          containerId: string
        ) => unknown;
        new TranslateElement(
          {
            pageLanguage: "en",
            layout: (translate.TranslateElement as Record<string, unknown>).InlineLayout,
          },
          "google_translate_element"
        );
      };

      const script = document.createElement("script");
      script.id = "google_translate_script";
      script.type = "text/javascript";
      script.defer = true;
      script.async = true;
      script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      document.head.appendChild(script);
    };

    // Returning visitor who is already reading a translated page?
    const hasTranslateCookie = document.cookie.includes("googtrans=");
    let switchedBefore = false;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) switchedBefore = JSON.parse(raw)?.pref === "switched";
    } catch {
      switchedBefore = false;
    }

    if (hasTranslateCookie || switchedBefore) {
      loadTranslate();
    }

    window.addEventListener(LOAD_TRANSLATE_EVENT, loadTranslate);

    // Google Translate keeps re-applying inline `top` on <body> to make room
    // for its banner. Force it back to 0 whenever it mutates.
    const enforceBodyTop = () => {
      if (document.body.style.top !== "0px" && document.body.style.top !== "") {
        document.body.style.top = "0px";
      }
    };
    const observer = new MutationObserver(enforceBodyTop);
    observer.observe(document.body, { attributes: true, attributeFilter: ["style"] });
    return () => {
      observer.disconnect();
      window.removeEventListener(LOAD_TRANSLATE_EVENT, loadTranslate);
    };
  }, []);

  return null;
}
